import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Activity, Loader2, Wrench, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import api from '@/services/api';

export const StockHealthTab = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get('/stock-audit/health').then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const repair = async () => {
    if (!window.confirm('Zero every negative stock row and move the deficit into that product\'s live batches at the same location?')) return;
    setBusy(true);
    try {
      const r = await api.post('/stock-audit/health/repair-negative');
      toast({ title: `Repaired ${r.data.fixed.length} row(s)`, description: r.data.unresolved.length ? `${r.data.unresolved.length} could not be fully resolved (no positive stock left) — run a Stock Audit there.` : 'All negative rows resolved.' });
      load();
    } catch (e) { toast({ title: 'Repair failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  return (
    <Card data-testid="stock-health-tab">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Activity className="w-5 h-5 text-emerald-600" /> Stock Health</CardTitle>
        <CardDescription>
          Earlier dispatches deducted the first matching stock row, which could be a damaged or empty batch — that row went negative while the live batches kept showing stock. This scan lists such rows; Repair moves the deficit into the live batches (total unchanged). For a full check use <b>Stock Audit</b> (physical count vs system) per store/warehouse.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2 items-center">
          <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-1" /> Rescan</Button>
          <Button onClick={repair} disabled={busy || !data?.negative_count} className="bg-amber-600 hover:bg-amber-700" data-testid="stock-health-repair-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Wrench className="w-4 h-4 mr-1" />} Repair negative rows ({data?.negative_count ?? '…'})
          </Button>
          {data && <span className="text-xs text-gray-500">{data.legacy_dispatches_without_deduction_record} dispatch(es) pre-date the deduction log — their stock is only verifiable via Stock Audit.</span>}
        </div>
        {data?.negative_rows?.length > 0 && (
          <Table data-testid="stock-health-table">
            <TableHeader><TableRow><TableHead>Location</TableHead><TableHead>Product</TableHead><TableHead>Batch</TableHead><TableHead className="text-right">Qty</TableHead><TableHead>Damaged row</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.negative_rows.map(r => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{r.location_type} · {r.warehouse_id || r.store_id}</TableCell>
                  <TableCell>{r.product_name || r.product_id}</TableCell>
                  <TableCell className="text-xs font-mono">{r.batch_number || r.lot_batch_number || '—'}</TableCell>
                  <TableCell className="text-right text-red-600 font-semibold">{r.quantity}</TableCell>
                  <TableCell>{r.is_damaged ? 'yes' : 'no'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {data && data.negative_count === 0 && <p className="text-sm text-emerald-700" data-testid="stock-health-ok">No negative stock rows found.</p>}
      </CardContent>
    </Card>
  );
};
